import axios from 'axios';
import { useAuth } from '@clerk/clerk-react';
import { useContext, useMemo } from 'react';
import { getApiConfig } from '../../app/services/environment';
import { UserContext } from '../../components/shared/useUser';
import {
  NewsArticle,
  NewsComment,
  NewsCommentsResult,
  NewsEngagementSummary,
  NewsFeedFilters,
  NewsFeedResult,
  NewsItemAnalytics,
} from '../../app/services/api/news/newsConsumerTypes';

interface SuccessResponse<T> {
  data: T;
}

export function useNewsConsumerClient() {
  const { getToken } = useAuth();
  const { forceLogout } = useContext(UserContext);
  const baseUrl = getApiConfig().apiBaseUrl;

  return useMemo(() => {
    const client = axios.create({ baseURL: baseUrl });

    client.interceptors.request.use(async (config) => {
      const token = await getToken({ template: 'Orderly_default' });
      const storedStore = localStorage.getItem('selectedStore');
      const store = storedStore ? JSON.parse(storedStore) : null;

      config.headers.Authorization = `Bearer ${token}`;
      config.headers.Accept = 'application/json';
      if (store) {
        config.headers['X-Context-Store-Number'] = store.storeNumber || '';
        config.headers['X-Context-Franchise-Id'] = store.franchiseId || '';
      }
      return config;
    });

    client.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) forceLogout();
        return Promise.reject(error);
      },
    );

    return {
      async getFeed(filters: NewsFeedFilters = {}): Promise<NewsFeedResult> {
        const response = await client.get<SuccessResponse<NewsFeedResult>>('/api/news-v2/feed', {
          params: {
            mode: filters.mode ?? 'Current',
            search: filters.search || undefined,
            tagId: filters.tagId,
            publishedFromUtc: filters.publishedFromUtc,
            publishedToUtc: filters.publishedToUtc,
            unreadOnly: filters.unreadOnly ?? false,
            storeNumber: filters.storeNumber || undefined,
            page: filters.page ?? 1,
            pageSize: filters.pageSize ?? 25,
          },
        });
        return response.data.data;
      },

      async getArticle(
        newsItemId: number,
        mode: 'Current' | 'History' = 'Current',
        storeNumber?: string,
      ): Promise<NewsArticle> {
        const response = await client.get<SuccessResponse<NewsArticle>>(
          `/api/news-v2/feed/${newsItemId}`,
          { params: { mode, storeNumber: storeNumber || undefined } },
        );
        return response.data.data;
      },

      async recordView(newsItemId: number, viewEventKey: string): Promise<void> {
        await client.post(
          `/api/news-v2/feed/${newsItemId}/view`,
          { viewEventKey },
        );
      },

      async getAnalytics(newsItemId: number): Promise<NewsItemAnalytics> {
        const response = await client.get<SuccessResponse<NewsItemAnalytics>>(
          `/api/news-v2/management/${newsItemId}/analytics`,
        );
        return response.data.data;
      },

      async getEngagement(newsItemId: number): Promise<NewsEngagementSummary> {
        const response = await client.get<SuccessResponse<NewsEngagementSummary>>(
          `/api/news-v2/feed/${newsItemId}/engagement`,
        );
        return response.data.data;
      },

      async setReaction(newsItemId: number, isLiked: boolean): Promise<NewsEngagementSummary> {
        const response = await client.post<SuccessResponse<NewsEngagementSummary>>(
          `/api/news-v2/feed/${newsItemId}/reaction`,
          { isLiked },
        );
        return response.data.data;
      },

      async getComments(newsItemId: number): Promise<NewsCommentsResult> {
        const response = await client.get<SuccessResponse<NewsCommentsResult>>(
          `/api/news-v2/feed/${newsItemId}/comments`,
        );
        return response.data.data;
      },

      async createComment(newsItemId: number, content: string, parentCommentId?: number): Promise<NewsComment> {
        const response = await client.post<SuccessResponse<NewsComment>>(
          `/api/news-v2/feed/${newsItemId}/comments`,
          { content, parentCommentId },
        );
        return response.data.data;
      },

      async setCommentReaction(newsItemId: number, commentId: number, isLiked: boolean): Promise<void> {
        await client.post(
          `/api/news-v2/feed/${newsItemId}/comments/${commentId}/reaction`,
          { isLiked },
        );
      },

      async updateComment(newsItemId: number, commentId: number, content: string, rowVersion: string): Promise<NewsComment> {
        const response = await client.put<SuccessResponse<NewsComment>>(
          `/api/news-v2/feed/${newsItemId}/comments/${commentId}`,
          { content, rowVersion },
        );
        return response.data.data;
      },

      async deleteOwnComment(newsItemId: number, commentId: number, rowVersion: string): Promise<void> {
        await client.delete(
          `/api/news-v2/feed/${newsItemId}/comments/${commentId}`,
          { data: { rowVersion } },
        );
      },

      async moderateComment(newsItemId: number, commentId: number, reason: string, rowVersion: string): Promise<void> {
        await client.post(
          `/api/news-v2/management/${newsItemId}/comments/${commentId}/moderate`,
          { reason, rowVersion },
        );
      },
    };
  }, [baseUrl, forceLogout, getToken]);
}
